import { supabase } from '../lib/supabase';
import { OrganizationService } from './organization.service';
import { Organization, OrganizationUpdate } from '../types/database.types';

// Extended types for comprehensive settings
export interface SystemPreferences {
  currency: string;
  date_format: string;
  timezone: string;
  language: string;
  measurement_units: 'metric' | 'imperial';

  // General Settings (EasyCars compatibility)
  require_delivery_date: boolean;
  use_actual_expenses_as_estimated: boolean;
  require_lead_source_on_sale: boolean;
  default_lot_load_fund: number;
  default_warranty_allowance: number;
  accounting_lock_date: string | null;

  // Advertising rules — which stock statuses stay live on the website / feeds
  auto_advertise_new_stock: boolean;   // list a car the moment it's added
  advertise_in_transit: boolean;       // keep advertising while In Transit / On Order
  advertise_on_offer: boolean;         // keep advertising after a deposit (On Offer)
}

export interface SecuritySettings {
  password_min_length: number;
  password_require_uppercase: boolean;
  password_require_lowercase: boolean;
  password_require_numbers: boolean;
  password_require_symbols: boolean;
  password_expiry_days: number;
  session_timeout_minutes: number;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  require_2fa: boolean;
  allowed_ip_addresses: string[];
}

export interface NotificationSettings {
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  notification_types: {
    new_leads: boolean;
    deal_updates: boolean;
    inventory_alerts: boolean;
    system_maintenance: boolean;
    payment_reminders: boolean;
    document_expiry: boolean;
  };
  digest_frequency: 'daily' | 'weekly' | 'monthly' | 'none';
  quiet_hours: {
    enabled: boolean;
    start_time: string;
    end_time: string;
  };
}

export interface IntegrationSettings {
  xero: {
    enabled: boolean;
    tenant_id?: string;
    auto_sync: boolean;
    sync_frequency_hours: number;
  };
  email_provider: {
    provider: 'smtp' | 'sendgrid' | 'mailgun' | 'aws_ses' | 'default';
    settings: Record<string, any>;
    enabled: boolean;
    // Email Preferences (EasyCars compatibility)
    send_copy_to_from?: boolean; // Send copy of outgoing emails to sender
    use_default_server?: boolean; // Use hosted default email server
    from_email?: string; // Default from email address
    from_name?: string; // Default from name
  };
  sms_provider: {
    provider: 'twilio' | 'nexmo' | 'aws_sns';
    settings: Record<string, any>;
    enabled: boolean;
  };
  crm_integrations: {
    hubspot: { enabled: boolean; api_key?: string; };
    salesforce: { enabled: boolean; credentials?: Record<string, any>; };
    pipedrive: { enabled: boolean; api_token?: string; };
  };
}

export interface DocumentTemplates {
  invoice_template: string;
  quote_template: string;
  contract_template: string;
  receipt_template: string;
  service_booking_template: string;
  custom_templates: Array<{
    id: string;
    name: string;
    type: string;
    template_content: string;
  }>;
}

export interface BackupSettings {
  auto_backup_enabled: boolean;
  backup_frequency: 'daily' | 'weekly' | 'monthly';
  backup_time: string;
  retention_days: number;
  backup_location: 'local' | 's3' | 'google_drive';
  backup_encryption: boolean;
  backup_notifications: boolean;
}

export interface StaffMember {
  id: string;
  user_id: string;
  employee_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role: 'owner' | 'admin' | 'manager' | 'sales' | 'service' | 'viewer';
  department?: string;
  permissions: string[];
  commission_rate?: number;
  targets?: Record<string, any>;
  status: 'active' | 'inactive' | 'suspended';
  hire_date?: string;
  employment_type: 'full_time' | 'part_time' | 'casual' | 'contractor';
}

export interface SettingsData {
  organization: Organization;
  system_preferences: SystemPreferences;
  security_settings: SecuritySettings;
  notification_settings: NotificationSettings;
  integration_settings: IntegrationSettings;
  document_templates: DocumentTemplates;
  backup_settings: BackupSettings;
  staff_members: StaffMember[];
}

class SettingsService {
  // Cache for integration secrets to avoid excessive Edge Function calls
  private integrationSecretsCache: {
    email?: any;
    sms?: any;
    timestamp?: number;
  } = {};

  // Cache for organization settings to avoid excessive database queries
  private organizationSettingsCache: Map<string, { settings: any; timestamp: number }> = new Map();

  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

  /**
   * Get cached organization settings or fetch from database
   * This prevents multiple queries for the same settings object
   */
  private async getCachedOrganizationSettings(orgId: string): Promise<any> {
    const now = Date.now();
    const cached = this.organizationSettingsCache.get(orgId);

    // Return cached if valid
    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      console.log('[SettingsService] Using cached organization settings for org:', orgId);
      return cached.settings;
    }

    // Cache miss or expired - fetch from database
    console.log('[SettingsService] Fetching organization settings from database for org:', orgId);
    const { data, error } = await supabase
      .from('organizations')
      .select('settings')
      .eq('id', orgId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching organization settings:', error);
      throw error;
    }

    // Update cache
    this.organizationSettingsCache.set(orgId, {
      settings: data?.settings || {},
      timestamp: now
    });

    return data?.settings || {};
  }

  /**
   * Invalidate cached organization settings (call after updates)
   */
  private invalidateOrganizationSettingsCache(orgId: string): void {
    this.organizationSettingsCache.delete(orgId);
  }

  // Organization/Business Information
  async getOrganization(): Promise<Organization | null> {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Now get the specific organization
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw error;
    }
  }

  async updateOrganization(updates: OrganizationUpdate): Promise<Organization> {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // First, verify the organization exists
      const { data: orgCheck, error: orgCheckError } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('id', organizationId)
        .maybeSingle();

      if (orgCheckError || !orgCheck) {
        console.error('Organization not found:', organizationId);
        throw new Error('Organization not found. Please contact support.');
      }

      // Now update the organization
      const { data, error } = await supabase
        .from('organizations')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', organizationId)
        .select();

      if (error) {
        console.error('Error updating organization:', error);
        throw new Error(`Failed to update organization: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Update succeeded but no data returned. This may be an RLS policy issue.');
      }

      return data[0];
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  // System Preferences
  async getSystemPreferences(orgId: string): Promise<SystemPreferences> {
    try {
      // Use cached settings to avoid repeated database queries
      const settings = await this.getCachedOrganizationSettings(orgId);

      const defaultPreferences: SystemPreferences = {
        currency: 'AUD',
        date_format: 'DD/MM/YYYY',
        timezone: 'Australia/Sydney',
        language: 'en-AU',
        measurement_units: 'metric',
        // General Settings - all optional, defaults to OFF/0/null
        require_delivery_date: false,
        use_actual_expenses_as_estimated: false,
        require_lead_source_on_sale: false,
        default_lot_load_fund: 0,
        default_warranty_allowance: 0,
        accounting_lock_date: null,
        // Advertising defaults — Phil wants stock live the moment it's bought
        auto_advertise_new_stock: true,
        advertise_in_transit: true,
        advertise_on_offer: true,
      };

      return {
        ...defaultPreferences,
        ...settings?.system_preferences
      };
    } catch (error) {
      console.error('Error fetching system preferences:', error);
      throw error;
    }
  }

  async updateSystemPreferences(orgId: string, preferences: Partial<SystemPreferences>): Promise<void> {
    try {
      // Invalidate cache since we're updating
      this.invalidateOrganizationSettingsCache(orgId);

      // Get current settings using cached method
      const settings = await this.getCachedOrganizationSettings(orgId);

      const currentSettings = settings || {};
      
      const { error } = await supabase
        .from('organizations')
        .update({
          settings: {
            ...currentSettings,
            system_preferences: {
              ...currentSettings.system_preferences,
              ...preferences
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', orgId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating system preferences:', error);
      throw error;
    }
  }

  // Security Settings
  async getSecuritySettings(orgId: string): Promise<SecuritySettings> {
    try {
      // Use cached settings to avoid repeated database queries
      const settings = await this.getCachedOrganizationSettings(orgId);

      const defaultSettings: SecuritySettings = {
        password_min_length: 8,
        password_require_uppercase: true,
        password_require_lowercase: true,
        password_require_numbers: true,
        password_require_symbols: false,
        password_expiry_days: 90,
        session_timeout_minutes: 60,
        max_login_attempts: 5,
        lockout_duration_minutes: 15,
        require_2fa: false,
        allowed_ip_addresses: []
      };

      return {
        ...defaultSettings,
        ...settings?.security_settings
      };
    } catch (error) {
      console.error('Error fetching security settings:', error);
      throw error;
    }
  }

  async updateSecuritySettings(orgId: string, settings: Partial<SecuritySettings>): Promise<void> {
    try {
      const { data: org } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', orgId)
        .maybeSingle();

      const currentSettings = org?.settings || {};
      
      const { error } = await supabase
        .from('organizations')
        .update({
          settings: {
            ...currentSettings,
            security_settings: {
              ...currentSettings.security_settings,
              ...settings
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', orgId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating security settings:', error);
      throw error;
    }
  }

  // Notification Settings
  async getNotificationSettings(orgId: string): Promise<NotificationSettings> {
    try {
      // Use cached settings to avoid repeated database queries
      const settings = await this.getCachedOrganizationSettings(orgId);

      const defaultSettings: NotificationSettings = {
        email_notifications: true,
        sms_notifications: false,
        push_notifications: true,
        notification_types: {
          new_leads: true,
          deal_updates: true,
          inventory_alerts: true,
          system_maintenance: true,
          payment_reminders: true,
          document_expiry: true
        },
        digest_frequency: 'daily',
        quiet_hours: {
          enabled: false,
          start_time: '18:00',
          end_time: '08:00'
        }
      };

      return {
        ...defaultSettings,
        ...settings?.notification_settings
      };
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      throw error;
    }
  }

  async updateNotificationSettings(orgId: string, settings: Partial<NotificationSettings>): Promise<void> {
    try {
      const { data: org } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', orgId)
        .maybeSingle();

      const currentSettings = org?.settings || {};
      
      const { error } = await supabase
        .from('organizations')
        .update({
          settings: {
            ...currentSettings,
            notification_settings: {
              ...currentSettings.notification_settings,
              ...settings
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', orgId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  // Staff Management
  async getStaffMembers(orgId: string): Promise<StaffMember[]> {
    try {
      // Get all users in this organization
      const { data: members, error: membersError } = await supabase
        .from('users')
        .select('*')
        .eq('organization_id', orgId);
      
      if (membersError) throw membersError;
      
      // Then get profiles for those users
      const userIds = (members || []).map(m => m.user_id).filter(Boolean);
      
      let profiles: any[] = [];
      if (userIds.length > 0) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .in('id', userIds);
        
        if (!profileError && profileData) {
          profiles = profileData;
        }
      }
      
      // Create a map of profiles by user_id for easy lookup
      const profileMap = new Map(profiles.map(p => [p.id, p]));
      
      // Transform the data to match our StaffMember interface
      return (members || []).map((member: any) => {
        const profile = profileMap.get(member.user_id);
        return {
          id: member.id,
          user_id: member.user_id,
          employee_id: member.employee_id,
          first_name: profile?.first_name || '',
          last_name: profile?.last_name || '',
          email: profile?.email || '',
          phone: profile?.phone || '',
          role: member.role,
          department: member.department,
          permissions: member.permissions || [],
          commission_rate: member.commission_rate,
          targets: member.targets,
          status: member.status,
          hire_date: member.joined_at,
          employment_type: 'full_time' // Default, could be added to schema
        };
      });
    } catch (error) {
      console.error('Error fetching staff members:', error);
      throw error;
    }
  }

  async updateStaffMember(memberId: string, updates: Partial<StaffMember>): Promise<void> {
    try {
      // First, get the user_id from the users record
      const { data: member, error: fetchError } = await supabase
        .from('users')
        .select('user_id')
        .eq('id', memberId)
        .single();

      if (fetchError) throw fetchError;
      if (!member?.user_id) throw new Error('User ID not found for staff member');

      // Update the profiles table with first_name, last_name, phone
      const profileUpdates: any = {};
      if (updates.first_name !== undefined) profileUpdates.first_name = updates.first_name;
      if (updates.last_name !== undefined) profileUpdates.last_name = updates.last_name;
      if (updates.phone !== undefined) profileUpdates.phone = updates.phone;

      if (Object.keys(profileUpdates).length > 0) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update(profileUpdates)
          .eq('id', member.user_id);

        if (profileError) throw profileError;
      }

      // Update the users table with role, department, and other fields
      const usersUpdates: any = {};
      if (updates.role !== undefined) usersUpdates.role = updates.role;
      if (updates.department !== undefined) usersUpdates.department = updates.department;
      if (updates.commission_rate !== undefined) usersUpdates.commission_rate = updates.commission_rate;
      if (updates.targets !== undefined) usersUpdates.targets = updates.targets;
      if (updates.status !== undefined) usersUpdates.status = updates.status;
      if (updates.employee_id !== undefined) usersUpdates.employee_id = updates.employee_id;

      if (Object.keys(usersUpdates).length > 0) {
        const { error: usersError } = await supabase
          .from('users')
          .update(usersUpdates)
          .eq('id', memberId);

        if (usersError) throw usersError;
      }
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  }

  async addStaffMember(orgId: string, staffData: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    department?: string;
  }): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // First, check if user already exists
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', staffData.email)
        .maybeSingle();

      const userId = existingUser?.id;

      if (!existingUser) {
        // User doesn't exist - in a real app, you'd send an invitation email
        // For now, we'll return a message about invitation
        return {
          success: false,
          message: `User with email ${staffData.email} not found. In a production app, an invitation would be sent.`
        };
      }

      // Check if user is already in this organization
      const { data: existingMember } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .eq('organization_id', orgId)
        .maybeSingle();

      if (existingMember) {
        return {
          success: false,
          message: 'User is already a member of this organization'
        };
      }

      // Update user with organization
      const { data, error } = await supabase
        .from('users')
        .update({
          organization_id: orgId,
          role: staffData.role
        })
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) throw error;

      return {
        success: true,
        message: 'Staff member added successfully',
        data
      };
    } catch (error) {
      console.error('Error adding staff member:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to add staff member'
      };
    }
  }

  // Integration Settings
  async getIntegrationSettings(orgId: string): Promise<IntegrationSettings> {
    try {
      // Use cached settings to avoid repeated database queries
      const settings = await this.getCachedOrganizationSettings(orgId);

      const defaultSettings: IntegrationSettings = {
        xero: {
          enabled: false,
          auto_sync: true,
          sync_frequency_hours: 24
        },
        email_provider: {
          provider: 'smtp',
          settings: {},
          enabled: false
        },
        sms_provider: {
          provider: 'twilio',
          settings: {},
          enabled: false
        },
        crm_integrations: {
          hubspot: { enabled: false },
          salesforce: { enabled: false },
          pipedrive: { enabled: false }
        }
      };

      const integrationSettings = {
        ...defaultSettings,
        ...settings?.integration_settings
      };

      // Check cache validity
      const now = Date.now();
      const isCacheValid = this.integrationSecretsCache.timestamp &&
        (now - this.integrationSecretsCache.timestamp) < this.CACHE_TTL;

      if (isCacheValid) {
        // Use cached values
        console.log('[SettingsService] Using cached integration secrets');
        if (this.integrationSecretsCache.email) {
          integrationSettings.email_provider = this.integrationSecretsCache.email;
        }
        if (this.integrationSecretsCache.sms) {
          integrationSettings.sms_provider = this.integrationSecretsCache.sms;
        }
      } else {
        // Fetch fresh data from Edge Functions
        console.log('[SettingsService] Fetching integration secrets from Edge Functions...');

        // Fetch both in parallel for better performance
        const [emailResult, smsResult] = await Promise.allSettled([
          supabase.functions.invoke('manage-integration-secrets', {
            body: {
              action: 'get',
              provider_type: 'email',
              reveal: false // Get masked values for client display
            }
          }),
          supabase.functions.invoke('manage-integration-secrets', {
            body: {
              action: 'get',
              provider_type: 'sms',
              reveal: false // Get masked values for client display
            }
          })
        ]);

        // Process email result
        if (emailResult.status === 'fulfilled' && emailResult.value?.data?.success && emailResult.value?.data?.data) {
          integrationSettings.email_provider = emailResult.value.data.data;
          this.integrationSecretsCache.email = emailResult.value.data.data;
        } else if (emailResult.status === 'rejected') {
          console.warn('Could not fetch email provider settings from Edge Function:', emailResult.reason);
        }

        // Process SMS result
        if (smsResult.status === 'fulfilled' && smsResult.value?.data?.success && smsResult.value?.data?.data) {
          integrationSettings.sms_provider = smsResult.value.data.data;
          this.integrationSecretsCache.sms = smsResult.value.data.data;
        } else if (smsResult.status === 'rejected') {
          console.warn('Could not fetch SMS provider settings from Edge Function:', smsResult.reason);
        }

        // Update cache timestamp
        this.integrationSecretsCache.timestamp = now;
      }

      return integrationSettings;
    } catch (error) {
      console.error('Error fetching integration settings:', error);
      throw error;
    }
  }

  async updateIntegrationSettings(orgId: string, settings: Partial<IntegrationSettings>): Promise<void> {
    try {
      // Handle email_provider and sms_provider through Edge Function for security
      if (settings.email_provider) {
        const { error } = await supabase.functions.invoke('manage-integration-secrets', {
          body: {
            action: 'save',
            provider_type: 'email',
            settings: settings.email_provider
          }
        });

        if (error) throw error;

        // Invalidate cache for email
        this.integrationSecretsCache.email = undefined;

        // Remove from settings object to avoid double-saving
        const { email_provider, ...remainingSettings } = settings;
        settings = remainingSettings;
      }

      if (settings.sms_provider) {
        const { error } = await supabase.functions.invoke('manage-integration-secrets', {
          body: {
            action: 'save',
            provider_type: 'sms',
            settings: settings.sms_provider
          }
        });

        if (error) throw error;

        // Invalidate cache for SMS
        this.integrationSecretsCache.sms = undefined;

        // Remove from settings object to avoid double-saving
        const { sms_provider, ...remainingSettings } = settings;
        settings = remainingSettings;
      }

      // Update any remaining settings (e.g., Xero, CRM) directly
      if (Object.keys(settings).length > 0) {
        const { data: org } = await supabase
          .from('organizations')
          .select('settings')
          .eq('id', orgId)
          .maybeSingle();

        const currentSettings = org?.settings || {};

        const { error } = await supabase
          .from('organizations')
          .update({
            settings: {
              ...currentSettings,
              integration_settings: {
                ...currentSettings.integration_settings,
                ...settings
              }
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', orgId);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error updating integration settings:', error);
      throw error;
    }
  }

  // Document Templates
  async getDocumentTemplates(orgId: string): Promise<DocumentTemplates> {
    try {
      // Use cached settings to avoid repeated database queries
      const settings = await this.getCachedOrganizationSettings(orgId);

      const defaultTemplates: DocumentTemplates = {
        invoice_template: 'default_invoice',
        quote_template: 'default_quote',
        contract_template: 'default_contract',
        receipt_template: 'default_receipt',
        service_booking_template: 'default_service',
        custom_templates: []
      };

      return {
        ...defaultTemplates,
        ...settings?.document_templates
      };
    } catch (error) {
      console.error('Error fetching document templates:', error);
      throw error;
    }
  }

  async updateDocumentTemplates(orgId: string, templates: Partial<DocumentTemplates>): Promise<void> {
    try {
      const { data: org } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', orgId)
        .maybeSingle();

      const currentSettings = org?.settings || {};
      
      const { error } = await supabase
        .from('organizations')
        .update({
          settings: {
            ...currentSettings,
            document_templates: {
              ...currentSettings.document_templates,
              ...templates
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', orgId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating document templates:', error);
      throw error;
    }
  }

  // Backup Settings
  async getBackupSettings(orgId: string): Promise<BackupSettings> {
    try {
      // Use cached settings to avoid repeated database queries
      const settings = await this.getCachedOrganizationSettings(orgId);

      const defaultSettings: BackupSettings = {
        auto_backup_enabled: false,
        backup_frequency: 'daily',
        backup_time: '02:00',
        retention_days: 30,
        backup_location: 's3',
        backup_encryption: true,
        backup_notifications: true
      };

      return {
        ...defaultSettings,
        ...settings?.backup_settings
      };
    } catch (error) {
      console.error('Error fetching backup settings:', error);
      throw error;
    }
  }

  async updateBackupSettings(orgId: string, settings: Partial<BackupSettings>): Promise<void> {
    try {
      const { data: org } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', orgId)
        .maybeSingle();

      const currentSettings = org?.settings || {};
      
      const { error } = await supabase
        .from('organizations')
        .update({
          settings: {
            ...currentSettings,
            backup_settings: {
              ...currentSettings.backup_settings,
              ...settings
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', orgId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating backup settings:', error);
      throw error;
    }
  }

  // Utility methods
  async getAllSettings(orgId: string): Promise<SettingsData> {
    try {
      const [
        organization,
        systemPreferences,
        securitySettings,
        notificationSettings,
        integrationSettings,
        documentTemplates,
        backupSettings,
        staffMembers
      ] = await Promise.all([
        this.getOrganization(),
        this.getSystemPreferences(orgId),
        this.getSecuritySettings(orgId),
        this.getNotificationSettings(orgId),
        this.getIntegrationSettings(orgId),
        this.getDocumentTemplates(orgId),
        this.getBackupSettings(orgId),
        this.getStaffMembers(orgId)
      ]);

      if (!organization) {
        throw new Error('Organization not found');
      }

      return {
        organization,
        system_preferences: systemPreferences,
        security_settings: securitySettings,
        notification_settings: notificationSettings,
        integration_settings: integrationSettings,
        document_templates: documentTemplates,
        backup_settings: backupSettings,
        staff_members: staffMembers
      };
    } catch (error) {
      console.error('Error fetching all settings:', error);
      throw error;
    }
  }

  // Test connection methods - DISABLED (not yet implemented)
  async testEmailConfiguration(settings: any): Promise<boolean> {
    // TODO: Implement real email configuration testing
    // This should send a test email and verify delivery
    throw new Error('Email configuration testing not yet implemented');
  }

  async testSMSConfiguration(settings: any): Promise<boolean> {
    // TODO: Implement real SMS configuration testing
    // This should send a test SMS and verify delivery
    throw new Error('SMS configuration testing not yet implemented');
  }
}

export const settingsService = new SettingsService();
export default settingsService;